/**
 * 权限矩阵：角色 × 可见范围 × 权限点 × 职责说明。
 *
 * 全部来自 `GET /api/admin/permissions`（仅超管可调，其他角色 403）。
 * **前端不硬编码任何角色或权限清单** —— 角色配置改了，本页跟着接口变，
 * 不需要改前端、也不需要「口口相传」。
 */
import { adminApi, ApiError } from '../lib/api.ts';
import type { RoleRow } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Card, PageHead, PermissionBlock } from '../components/ui.tsx';
import { EmptyBox, ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import { scopeLabel, plainText } from '../lib/format.ts';

/**
 * 字段组 → 所需权限点。这是「权限点命名与字段组的对应关系」，
 * 与 apps/api/src/core/rbac.ts 的权限点定义一一对应；
 * 能看哪些字段的角色**由接口返回的权限点反推**，不由前端列举角色名。
 */
const FIELD_GROUPS: { label: string; detail: string; permission: string }[] = [
  {
    label: '健康',
    detail: 'H 分、健康等级、生物年龄、器官年龄、未解除预警数',
    permission: 'member:read_health',
  },
  {
    label: '资产',
    detail: 'W 分、资产等级、健康信托方案',
    permission: 'member:read_wealth',
  },
  {
    label: '会籍与联络',
    detail: '会籍等级、城市等运营必需字段',
    permission: 'member:read',
  },
];

/** 具备某权限点的角色名，直接从接口返回的角色定义里筛，避免在前端写死角色清单。 */
function rolesWith(roles: RoleRow[], permission: string): string {
  const names = roles.filter((role) => role.permissions.includes(permission)).map((role) => role.name);
  return names.length > 0 ? names.join('、') : '（当前无角色具备该权限点）';
}

export function PermissionsPage() {
  const { data, error, loading, reload } = useLoad((signal) => adminApi.permissions(signal));

  if (loading) return <LoadingBox text="正在加载权限矩阵…" />;

  if (error) {
    if (error instanceof ApiError && error.status === 403) {
      return (
        <div>
          <PageHead title="权限矩阵" sub="403 · 仅超级管理员可查看" />
          <Card>
            <PermissionBlock
              title="当前角色无权查看权限矩阵"
              desc={`服务端返回：${error.message}。权限矩阵涉及全部角色的权限点配置，只向具备 staff:manage 权限点的角色开放；可见范围也不影响这一限制。`}
            />
            <div className="sp12" />
            <button type="button" className="btn" onClick={reload}>
              重新校验
            </button>
          </Card>
        </div>
      );
    }
    return <ErrorBox message={errorText(error)} onRetry={reload} />;
  }

  if (!data || data.roles.length === 0) {
    return <EmptyBox title="权限矩阵为空" desc="服务端未返回任何角色定义，请检查角色配置。" />;
  }

  const roles = data.roles;

  return (
    <div>
      <PageHead
        title="权限矩阵"
        sub="角色决定「能不能做」，可见范围决定「能对谁做」；两者同时成立才放行"
        actions={<span className="badge e">共 {roles.length} 个角色定义</span>}
      />

      <Card tight>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>角色</th>
                <th>可见范围</th>
                <th>权限点</th>
                <th>职责说明</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.role}>
                  <td>
                    <b>{role.name}</b>
                    <div className="muted text-tiny mono-num">{role.role}</div>
                  </td>
                  <td>
                    <span className="badge ghost">{scopeLabel(role.scope)}</span>
                  </td>
                  <td>
                    <div className="perm-cells">
                      {role.permissions.length === 0 ? (
                        <span className="no-perm">无任何权限点</span>
                      ) : (
                        role.permissions.map((permission) => (
                          <span key={permission} className="badge e mono-num">
                            {permission}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="duty">{plainText(role.duty)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="敏感字段分级" sub="裁剪在服务端完成">
        <table className="tbl">
          <thead>
            <tr>
              <th>字段组</th>
              <th>内容</th>
              <th>所需权限点</th>
              <th>具备该权限点的角色</th>
            </tr>
          </thead>
          <tbody>
            {FIELD_GROUPS.map((group) => (
              <tr key={group.permission}>
                <td>
                  <b>{group.label}</b>
                </td>
                <td>{group.detail}</td>
                <td>
                  <span className="badge e mono-num">{group.permission}</span>
                </td>
                <td>{rolesWith(roles, group.permission)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="note-src">
          <b>为什么把这张表放在界面上</b>
          <br />
          字段级授权如果没有可见的清单，就只能靠口口相传，实际执行时必然走样。
          本页的角色、可见范围、权限点、职责说明，以及「哪些角色能看到哪组字段」，
          <b>全部由权限矩阵接口推导</b>，前端不写死角色清单与权限清单。
        </div>
      </Card>

      <div className="note-src">
        <b>最小必要</b>
        <br />
        员工每次读取成员档案与每次写操作都会写入审计链，携带被访问成员、员工工号、
        姓名 + 角色与查看目的；成员可在自己的「数据调用记录」里看到这些记录。
      </div>
    </div>
  );
}
